import type {EditableShortcutField, UserShortcut} from '@github-ui/saved-views'
import {useCallback, useMemo, useState} from 'react'

import type {PreviewQueryContextValue} from '../contexts/PreviewQueryContext'

interface UseSavedViewEditorOptions {
  initialShortcut: UserShortcut | (() => UserShortcut)
}

export interface UseSavedViewEditorReturn {
  shortcut: UserShortcut
  setShortcutValue: (field: EditableShortcutField, value: string) => void
  previewQuery: string
  commitPreviewQuery: (next: string) => void
  previewQueryContextValue: PreviewQueryContextValue
  isDirty: boolean
}

export function useSavedViewEditor({
  initialShortcut: initialShortcutProp,
}: UseSavedViewEditorOptions): UseSavedViewEditorReturn {
  const [initialShortcut] = useState<UserShortcut>(initialShortcutProp)
  const [shortcut, setShortcut] = useState<UserShortcut>(initialShortcut)
  const [previewQuery, setPreviewQuery] = useState<string>(() => initialShortcut.query.trim())

  const setShortcutValue = useCallback((field: EditableShortcutField, value: string) => {
    setShortcut(prev => ({...prev, [field]: value}))
  }, [])

  const commitPreviewQuery = useCallback((next: string) => {
    const trimmed = next.trim()
    setShortcut(prev => (prev.query === trimmed ? prev : {...prev, query: trimmed}))
    setPreviewQuery(trimmed)
  }, [])

  const previewQueryContextValue = useMemo(
    () => ({currentQuery: previewQuery, onQueryChange: commitPreviewQuery}),
    [previewQuery, commitPreviewQuery],
  )

  const isDirty =
    shortcut.name !== initialShortcut.name ||
    (shortcut.description ?? '') !== (initialShortcut.description ?? '') ||
    shortcut.query !== initialShortcut.query ||
    shortcut.icon !== initialShortcut.icon ||
    shortcut.color !== initialShortcut.color

  return {
    shortcut,
    setShortcutValue,
    previewQuery,
    commitPreviewQuery,
    previewQueryContextValue,
    isDirty,
  }
}
